import { AnfrageDisplayModel, AnredeDisplayModel,MitantragstellerDisplayModel, MitantragstellerDisplayModelFormGroup } from '@/api/model-index';
import { AnfrageService, MitantragstellerService } from '@/api/service-index';
import { Constants } from '@/constants';
import { AnfrageNavigationFormComponent, MoveDirection } from '@/core/components/anfrage-navigation-form/anfrage-navigation-form.component';
import { ConfirmationDialogAction, ConfirmationDialogComponent, ConfirmationDialogResult } from '@/core/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { AnfrageTransformerService } from '@/core/services/anfrage-transformer.service';
import { ComponentService } from '@/core/services/component.service';
import { I18nService } from '@/core/services/i18nService';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { SectionService } from '@/core/services/section.service';
import { StammdatenService } from '@/core/services/stammdaten.service';
import { StateService } from '@/core/services/state.service';
import { TextBoxService } from '@/core/services/textbox.service';
import { UnfinishedHttpMethodsService } from '@/core/services/unfinished-http-methods.service';
import { emailValidator } from '@/core/validators/customform.validators';
import { Component } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '@progress/kendo-angular-dialog';
import { GridDataResult, RemoveEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';

@Component({
    selector: 'mitantragsteller-process-step',
    templateUrl: './mitantragsteller-process-step.component.html',
    styleUrls: ['./mitantragsteller-process-step.component.scss'],
    standalone: false
})
export class MitantragstellerProcessStepComponent extends AnfrageNavigationFormComponent {
  public mitantragsteller: MitantragstellerDisplayModel[] = [];
  public mitantragstellerForm: MitantragstellerDisplayModelFormGroup;
  public anfrage: AnfrageDisplayModel;
  public gridView: GridDataResult;
  public isFormOpen: boolean = false;

  protected languageSubscription: Subscription;

  protected async openAenderungsantrag(id: string) {
    await this.sectionService.jumpToSection(this.router, this.route, id);
  }

  anredeItems: AnredeDisplayModel[];

  public get formGroup(): FormGroup<any> {
    return this.mitantragstellerForm;
  }

  public sort: SortDescriptor[] = [
    { field: "firstname", dir: "asc" }
  ];

  constructor(
    protected sectionService: SectionService,
    protected route: ActivatedRoute,
    protected loadingAnimationService: LoadingAnimationService,
    protected router: Router,
    protected stateService: StateService,
    protected mitantragstellerService: MitantragstellerService,
    protected portalContextService: PortalContextService,
    protected unfinishedHttpMethodsService: UnfinishedHttpMethodsService,
    protected languageService: I18nService,
    protected dialogService: DialogService,
    protected textBoxService: TextBoxService,
    protected componentService: ComponentService,
    protected anfrageService: AnfrageService,
    protected stammdatenService: StammdatenService,
    protected anfrageTransformerService: AnfrageTransformerService
  ) {
    super(sectionService, router, route, portalContextService);
    this.mitantragstellerForm = MitantragstellerDisplayModelFormGroup.Instance;
    this.anfrage = this.route.snapshot.data.anfrage;
    this.mitantragsteller = this.route.snapshot.data.mitantragsteller;

    this.initGridView();
  }

  override async canDeactivate(): Promise<boolean> {
    if (!this.mitantragstellerForm.valid && this.isFormOpen) {
      return this.openUnsavedChangesDialog();
    }
    return true;
  }

  get emailErrorMessage(): string {
    if (this.mitantragstellerForm.EmailAddress?.errors?.email)
      return this.languageService.instant('contactinformation.emailAddress.invalid');

    return "";
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadAnreden();

    this.languageSubscription = this.languageService.languageChanged$.subscribe(() => {
      this.loadAnreden();
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.languageSubscription?.unsubscribe();
  }

  protected loadAnreden(): void {
    this.stammdatenService.GetAnreden().subscribe(anreden => this.anredeItems = this.languageService.filterAnredenKeepSpecificId(anreden, this.mitantragstellerForm.AnredeId.value));
  }

  protected initGridView() {
    this.gridView = {
      data: this.mitantragsteller,
      total: this.mitantragsteller.length,
    };
  }

  protected removeHandler({ dataItem }: RemoveEvent): void {
    let deleteDialogRef = this.dialogService.open({
      content: this.componentService.get(ConfirmationDialogComponent),
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      title: this.languageService.instant("mitantragsteller.dialog.titleDelete")
    });
    let dialogComponent: ConfirmationDialogComponent = deleteDialogRef.content.instance;

    dialogComponent.content = this.languageService.instant("mitantragsteller.dialog.contentDelete");

    deleteDialogRef.result.subscribe(result => this.onDeleteDialogClose(<ConfirmationDialogResult>result, dataItem));
  }

  protected onDeleteDialogClose(result: ConfirmationDialogResult, dataItem: MitantragstellerDisplayModel) {
    if (result.action == ConfirmationDialogAction.Confirm) {
      const subscription = this.mitantragstellerService.apiMitantragstellerDeleteMitantragstellerAnfrageIdIdDelete({ id: dataItem.id!, anfrageId: this.portalContextService.CurrentAnfrage.id }).subscribe(() => {
        const index = this.mitantragsteller.findIndex(item => item.id === dataItem.id);
        if (index !== -1) {
          const removed = this.mitantragsteller.splice(index, 1);
          if (this.isFormOpen && removed[0].id === this.mitantragstellerForm.value.id) {
            this.isFormOpen = false;
          }
          this.initGridView();
        }

        this.portalContextService.CurrentAnfrage.mitantragsteller = this.mitantragsteller;
        this.anfrageTransformerService.transform(this.portalContextService.CurrentAnfrage).then(transformedAnfrage => {
          this.portalContextService.CurrentAnfrage = transformedAnfrage;
        });
      });

      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  public createHandler() {
    this.mitantragstellerForm = MitantragstellerDisplayModelFormGroup.Instance;
    this.mitantragstellerForm.AnredeId.addValidators(Validators.required);
    this.mitantragstellerForm.Firstname.addValidators(Validators.required);
    this.mitantragstellerForm.Lastname.addValidators(Validators.required);
    this.mitantragstellerForm.EmailAddress.addValidators([Validators.required, emailValidator]);

    this.isFormOpen = true;
  }

  public cancelForm() {
    this.isFormOpen = false;
  }

  public saveMitantragsteller(openAfterSave: boolean = false): void {
    const mitantragsteller: MitantragstellerDisplayModel = this.mitantragstellerForm.value;
    this.isFormOpen = false;

    let subscription = this.mitantragstellerService.apiMitantragstellerSaveMitantragstellerAnfrageIdPost({ anfrageId: this.anfrage.id, body: mitantragsteller }).subscribe(async (response) => {
      if (response && !mitantragsteller.id) {
        mitantragsteller.id = response;
        this.mitantragsteller.push(mitantragsteller);
      }
      else {
        const index = this.mitantragsteller.findIndex(item => item.id === mitantragsteller.id);
        if (index !== -1) {
          this.mitantragsteller[index] = mitantragsteller;
        }
      }

      
      this.initGridView();
      
      this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.anfrage.id }).subscribe(result => {
        this.stateService.ValidationErrors = result;
      })
      
      this.portalContextService.CurrentAnfrage.mitantragsteller = this.mitantragsteller;
      await this.anfrageTransformerService.transform(this.portalContextService.CurrentAnfrage).then(async transformedAnfrage => {
        this.portalContextService.CurrentAnfrage = transformedAnfrage;
        if (openAfterSave && mitantragsteller.id) {
          await this.openAenderungsantrag(mitantragsteller.id);
        }
      });
    });

    this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    this.unfinishedHttpMethodsService.addSubscription(subscription);
  }

  protected save(shouldSave: boolean, moveDirection: MoveDirection): void {
    // Nothing to save on that form 
    this.move(moveDirection);

    if (shouldSave && this.mitantragstellerForm.valid && this.isFormOpen) {
      this.saveMitantragsteller();
    }
  }
}
